// ============================================================================
// PresenterLibrary — checkbox picker over the presenter library.
// ============================================================================
// Sits inside EventForm above the Presenters markdown textarea. UIF clicks
// presenter cards to select them, then clicks "Insert selected as markdown"
// to overwrite the textarea with library-derived markdown. Edits afterwards
// are free-form — the textarea is the source of truth for the event.
//
// Also surfaces a "Manage library" button that opens PresenterManageModal
// for adding/editing/archiving the underlying library entries.
// ============================================================================

import { useEffect, useState } from 'react';
import { Settings2, Wand2, RefreshCw } from 'lucide-react';
import { api, type Presenter } from '../api';
import { PresenterManageModal } from './PresenterManageModal';

interface Props {
  token: string;
  value: string;                       // current presenters_md
  onChange: (next: string) => void;    // setter for presenters_md
  disabled?: boolean;
}

// Render a single presenter as a markdown section. Trailing two newlines are
// left off — the caller joins sections with '\n\n---\n\n'.
function presenterToMarkdown(p: Presenter): string {
  const parts: string[] = [];
  parts.push(`## ${p.name}`);
  if (p.role) parts.push(`*${p.role}*`);
  if (p.photo_url) parts.push(`![${p.name}](${p.photo_url})`);
  if (p.bio) parts.push(p.bio.trim());
  return parts.join('\n\n');
}

export function PresenterLibrary({ token, value, onChange, disabled }: Props) {
  const [presenters, setPresenters] = useState<Presenter[]>([]);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [manageOpen, setManageOpen] = useState(false);

  const load = () => {
    setLoading(true);
    setError('');
    api
      .presenters()
      .then((d) => setPresenters(d.presenters ?? []))
      .catch((err) => setError(err?.message || 'Could not load presenters.'))
      .finally(() => setLoading(false));
  };

  // Initial load. When the manage modal closes after an edit/add, we reload
  // explicitly via onRefresh in that handler.
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggle = (id: number) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };

  const insertSelected = () => {
    if (selected.size === 0) return;
    // Preserve display order from the library by filtering in source order.
    const chosen = presenters.filter((p) => selected.has(p.presenter_id));
    const md = chosen.map(presenterToMarkdown).join('\n\n---\n\n') + '\n';
    if (value.trim().length > 0) {
      const yes = window.confirm(
        'Replace the existing Presenters content with markdown generated from your selection?',
      );
      if (!yes) return;
    }
    onChange(md);
  };

  return (
    <div className="border border-gray-200 rounded-md p-3 bg-gray-50 space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-xs font-semibold text-gray-700 uppercase tracking-wider">
          Presenter library
        </span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={load}
            disabled={loading || disabled}
            title="Refresh list"
            className="inline-flex items-center gap-1 rounded border border-gray-300 px-2 py-1 text-xs text-gray-700 hover:bg-white disabled:opacity-50"
          >
            <RefreshCw className={'h-3 w-3 ' + (loading ? 'animate-spin' : '')} />
            Refresh
          </button>
          <button
            type="button"
            onClick={() => setManageOpen(true)}
            disabled={disabled}
            className="inline-flex items-center gap-1 rounded border border-gray-300 px-2 py-1 text-xs text-gray-700 hover:bg-white disabled:opacity-50"
          >
            <Settings2 className="h-3 w-3" />
            Manage library
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded px-2 py-1 text-xs">
          {error}
        </div>
      )}

      {loading && presenters.length === 0 ? (
        <p className="text-sm text-gray-500 italic">Loading presenters…</p>
      ) : presenters.length === 0 ? (
        <p className="text-sm text-gray-500 italic">
          No presenters yet. Click <em>Manage library</em> to add some.
        </p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {presenters.map((p) => {
            const isSelected = selected.has(p.presenter_id);
            return (
              <button
                key={p.presenter_id}
                type="button"
                onClick={() => toggle(p.presenter_id)}
                disabled={disabled}
                className={
                  'flex items-center gap-2 text-left rounded-md border p-2 transition-colors ' +
                  (isSelected
                    ? 'border-uif-green bg-uif-green/10'
                    : 'border-gray-300 bg-white hover:border-uif-green/40')
                }
              >
                {p.photo_url ? (
                  <img
                    src={p.photo_url}
                    alt={p.name}
                    className="h-10 w-10 rounded-full object-cover flex-shrink-0"
                    onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                  />
                ) : (
                  <div className="h-10 w-10 rounded-full bg-uif-green/10 text-uif-green-dark flex items-center justify-center text-xs font-bold flex-shrink-0">
                    {p.name.split(/\s+/).map((s) => s[0]).filter(Boolean).slice(0, 2).join('').toUpperCase()}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-gray-900 truncate">{p.name}</div>
                  {p.role && <div className="text-xs text-gray-600 truncate">{p.role}</div>}
                </div>
                <input
                  type="checkbox"
                  checked={isSelected}
                  readOnly
                  className="h-4 w-4 accent-uif-green flex-shrink-0"
                />
              </button>
            );
          })}
        </div>
      )}

      {presenters.length > 0 && (
        <div className="flex items-center justify-between gap-2 pt-1 border-t border-gray-200">
          <span className="text-xs text-gray-600">
            {selected.size === 0
              ? 'Select presenters above, then insert.'
              : `${selected.size} selected`}
          </span>
          <button
            type="button"
            onClick={insertSelected}
            disabled={disabled || selected.size === 0}
            className="inline-flex items-center gap-1 rounded-md bg-uif-green text-white px-3 py-1.5 text-xs font-semibold hover:bg-uif-green-dark disabled:bg-gray-300"
          >
            <Wand2 className="h-3.5 w-3.5" />
            Insert selected as markdown
          </button>
        </div>
      )}

      <PresenterManageModal
        open={manageOpen}
        onClose={() => setManageOpen(false)}
        token={token}
        presenters={presenters}
        onRefresh={load}
      />
    </div>
  );
}
